import { Admin } from "./types";
import fetchWrapper from "src/utils/fetchWrapper";
import { localNow } from "src/utils/serverClock";

const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 60000;
// Edit jobs confirm propagation across several projections; poll gently.
const EDIT_POLL_INTERVAL_MS = 3000;

export const AdminActions = {
    // Operator-driven password-reset email for one cpty user (migration
    // re-onboarding). Posts to auth-gateway (same origin as the admin portal);
    // ttlDays is the reset-link lifetime.
    SendPasswordReset: (cptyCode, email, subject, body, ttlSeconds) => {
        return async (dispatch) => {
            return await dispatch(
                fetchWrapper("/auth/admin/send-reset", {
                    method: "POST",
                    body: JSON.stringify({
                        cptyCode,
                        email,
                        subject,
                        body,
                        ttlSeconds,
                    }),
                })
            );
        };
    },

    FetchCpties: () => {
        return async (dispatch, getState) => {
            dispatch({ type: Admin.SET_LOADING, loading: true });
            try {
                // /cpties-summary returns every cpty in one response with only
                // the fields the table renders (code, name, mail domains,
                // lifecycle, purgeAt). The full entry loads on-demand via
                // FetchCptyDetail when the operator opens Manage.
                const url = getState().Auth.adminServiceUrl + "/cpties-summary";
                const resp = await dispatch(fetchWrapper(url));
                dispatch({
                    type: Admin.SET_CPTIES,
                    cpties: Array.isArray(resp?.data) ? resp.data : [],
                    purgeGraceSecs: resp?.purgeGraceSecs,
                });
            } catch (error) {
                console.error("Failed to fetch cpties:", error);
                dispatch({ type: Admin.SET_ERROR, error: error.message });
            } finally {
                dispatch({ type: Admin.SET_LOADING, loading: false });
            }
        };
    },

    FetchCptyDetail: (code) => {
        return async (dispatch, getState) => {
            dispatch({ type: Admin.SET_LOADING, loading: true });
            try {
                const url = getState().Auth.adminServiceUrl + "/cpties/" + code;
                const cpty = await dispatch(fetchWrapper(url));
                // Also fetch relationships and partners
                const relUrl = getState().Auth.adminServiceUrl + "/cpties/" + code + "/relationships";
                const partUrl = getState().Auth.adminServiceUrl + "/cpties/" + code + "/partners";
                let relationships = [];
                let partners = [];
                try {
                    relationships = await dispatch(fetchWrapper(relUrl));
                } catch (e) {
                    console.warn("Failed to fetch relationships:", e);
                }
                try {
                    partners = await dispatch(fetchWrapper(partUrl));
                } catch (e) {
                    console.warn("Failed to fetch partners:", e);
                }
                let accounts = [];
                try {
                    const acctUrl = getState().Auth.adminServiceUrl + "/cpties/" + code + "/accounts";
                    accounts = await dispatch(fetchWrapper(acctUrl));
                } catch (e) {
                    console.warn("Failed to fetch accounts:", e);
                }
                dispatch({ type: Admin.SET_SELECTED_CPTY, cpty: { ...cpty, relationships, partners, accounts } });
            } catch (error) {
                console.error("Failed to fetch cpty detail:", error);
                dispatch({ type: Admin.SET_ERROR, error: error.message });
            } finally {
                dispatch({ type: Admin.SET_LOADING, loading: false });
            }
        };
    },

    /**
     * Edit an existing cpty through the same setup-own-cpty flow used for
     * onboarding: POST the full desired state (isExisting=true), then poll
     * the job until the backend confirms every change actually propagated.
     * The backend publishes commands only for the sections that differ, so
     * a no-op save completes immediately.
     */
    EditCpty: (cptyCode, sections, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            const job = await dispatch(fetchWrapper(apiBase + "/setup-own-cpty", {
                method: "POST",
                headers: { "x-reason": reason || "" },
                body: JSON.stringify({ isExisting: true, cptyCode, ...sections }),
            }));
            if (job.status === "COMPLETED") return job;

            const deadline = localNow() + POLL_TIMEOUT_MS;
            while (localNow() < deadline) {
                await new Promise((r) => setTimeout(r, EDIT_POLL_INTERVAL_MS));
                const status = await dispatch(
                    fetchWrapper(apiBase + "/setup-own-cpty/" + job.jobId)
                );
                if (status.status === "FAILED") {
                    throw new Error((status.errors || []).join(", ") || "Edit failed");
                }
                if (status.status === "COMPLETED") return status;
            }
            throw new Error("Edit timed out - changes not confirmed propagated");
        };
    },

    /**
     * Set or replace a cpty's Generic API key. The plaintext is posted once
     * and stored server-side as a one-way HMAC; it is never retrievable
     * afterwards. Used by the Manage page's API Access section.
     */
    SetApiKey: (code, apiKey, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(apiBase + "/cpties/" + code + "/api-key", {
                method: "PUT",
                headers: { "x-reason": reason || "" },
                body: JSON.stringify({ apiKey }),
            }));
        };
    },

    /** Global platform settings for the Platform tab: returns
     *  { hfsEnabled, hfsKeyConfigured }. Never returns key material. */
    FetchPlatformSettings: () => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(fetchWrapper(apiBase + "/platform-settings"));
        };
    },

    /** Every published terms version, newest first. */
    FetchTermsVersions: () => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(fetchWrapper(apiBase + "/platform-settings/terms"));
        };
    },

    /**
     * Publish the four legal documents as a new version. The version name is
     * generated by the server, so nothing is sent but the documents.
     */
    PublishTerms: (documents, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(fetchWrapper(apiBase + "/platform-settings/terms", {
                method: "POST",
                headers: { "x-reason": reason || "" },
                body: JSON.stringify(documents),
            }));
        };
    },

    /** Global master switch for the shared HFS API. */
    SetPlatformHfsEnabled: (enabled, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(apiBase + "/platform-settings/hfs", {
                method: "PUT",
                headers: { "x-reason": reason || "" },
                body: JSON.stringify({ enabled }),
            }));
        };
    },

    /** Set/replace the single shared HFS API key. Stored as a one-way HMAC. */
    SetPlatformHfsApiKey: (apiKey, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(apiBase + "/platform-settings/hfs-api-key", {
                method: "PUT",
                headers: { "x-reason": reason || "" },
                body: JSON.stringify({ apiKey }),
            }));
        };
    },

    /** Offer-expiry timeout (seconds), read live at offer creation. */
    SetPlatformOfferExpiry: (offerExpirySecs, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(apiBase + "/platform-settings/offer-expiry", {
                method: "PUT",
                headers: { "x-reason": reason || "" },
                body: JSON.stringify({ offerExpirySecs }),
            }));
        };
    },

    /** Daily stale-tx deletion hour (0-23, Asia/Seoul), read live when a
     *  negotiation's end-of-day deletion timer is registered. */
    SetPlatformDailyDeletionHour: (dailyDeletionHourKst, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(apiBase + "/platform-settings/daily-deletion-hour", {
                method: "PUT",
                headers: { "x-reason": reason || "" },
                body: JSON.stringify({ dailyDeletionHourKst }),
            }));
        };
    },

    /** Per-provider market-data config for the Platform tab. Returns
     *  { providers: [{ provider, enabled, secretsConfigured }] }. Never returns
     *  secret values. */
    FetchMarketDataProviders: () => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(fetchWrapper(apiBase + "/platform-settings/market-data"));
        };
    },

    /** Set a market-data provider's enable toggle and/or secrets. `secrets` is a
     *  map of field name -> value; omit it to change only the toggle (stored
     *  secrets are preserved). Secrets are stored reversibly-encrypted server
     *  side and never returned. */
    SetMarketDataProvider: (provider, { enabled, secrets }, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            const body = {};
            if (enabled !== undefined) body.enabled = enabled;
            if (secrets !== undefined) body.secrets = secrets;
            await dispatch(
                fetchWrapper(apiBase + "/platform-settings/market-data/" + provider, {
                    method: "PUT",
                    headers: { "x-reason": reason || "" },
                    body: JSON.stringify(body),
                })
            );
        };
    },

    /** Market-data schedule rules. Returns { rules: [{ id, function, provider,
     *  market, runTimes:[HH:MM], timezone, enabled }], statuses: { ruleId: Status } }
     *  where Status is "Pending (first run)" | "Healthy" | "Failed". */
    FetchMarketDataSchedule: () => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(
                fetchWrapper(apiBase + "/platform-settings/market-data/schedule")
            );
        };
    },

    /** Recent import runs for one schedule rule (View History). Returns
     *  { runs: [{ ruleId, scheduledFor, runAt, status, detail, finishedAt, itemCount }] }. */
    FetchMarketDataScheduleHistory: (id) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(
                fetchWrapper(
                    apiBase + "/platform-settings/market-data/schedule/"
                        + encodeURIComponent(id) + "/history"
                )
            );
        };
    },

    /** Run one already-defined task now (the Platform tab "Run now"). Generic so
     *  it can serve future task types; today `task` is a market-data import
     *  { id, function, provider, market }. Publishes an EXECUTE_JOB command via
     *  admin-service; the scheduler runs it and records the outcome in history. */
    RunTask: (task, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(
                fetchWrapper(apiBase + "/run-task", {
                    method: "POST",
                    headers: { "x-reason": reason || "" },
                    body: JSON.stringify(task),
                })
            );
        };
    },

    /** Create/update a schedule rule (upsert; id derived from
     *  function:provider:market when omitted). */
    SaveMarketDataScheduleRule: (rule, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(
                fetchWrapper(apiBase + "/platform-settings/market-data/schedule", {
                    method: "PUT",
                    headers: { "x-reason": reason || "" },
                    body: JSON.stringify(rule),
                })
            );
        };
    },

    /** Delete a schedule rule by id. */
    DeleteMarketDataScheduleRule: (id, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            await dispatch(
                fetchWrapper(
                    apiBase + "/platform-settings/market-data/schedule/" + encodeURIComponent(id),
                    { method: "DELETE", headers: { "x-reason": reason || "" } }
                )
            );
        };
    },

    /** Recurring platform-command schedules (issue #31). Returns
     *  { commands: [name], schedules: { name: {enabled, time, timezone, cadence,
     *  daysOfWeek, nthWeekday} }, statuses: { name: "Healthy"|... } }. The same
     *  config scheduler-service reads from platform_settings each tick. */
    FetchCommandSchedules: () => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(fetchWrapper(apiBase + "/platform-settings/command-schedules"));
        };
    },

    /** Replace the whole command-schedule config (map keyed by command name).
     *  The Platform tab edits every command together and confirms once. */
    SetCommandSchedules: (schedules, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            await dispatch(
                fetchWrapper(apiBase + "/platform-settings/command-schedules", {
                    method: "PUT",
                    headers: { "x-reason": reason || "" },
                    body: JSON.stringify(schedules),
                })
            );
        };
    },

    /** Recent fired occurrences for one scheduled command (View History).
     *  Returns { runs: [{ ruleId, scheduledFor, runAt, status, detail, ... }] }. */
    FetchCommandScheduleHistory: (command) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(
                fetchWrapper(
                    apiBase + "/platform-settings/command-schedules/"
                        + encodeURIComponent(command) + "/history"
                )
            );
        };
    },

    /** Run one scheduled command now (Platform tab "Run now"), bypassing cadence.
     *  Publishes RUN_SCHEDULED_COMMAND; the scheduler fires it and records history. */
    RunCommandScheduleNow: (command, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(
                fetchWrapper(
                    apiBase + "/platform-settings/command-schedules/"
                        + encodeURIComponent(command) + "/run",
                    { method: "POST", headers: { "x-reason": reason || "" } }
                )
            );
        };
    },

    /** The user-access report's runs for one counterparty, newest first. It fires
     *  per cpty, so its history lives under the key `SEND_USER_REPORT:<cpty>`. */
    FetchUserReportHistory: (cptyCode) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(
                fetchWrapper(
                    apiBase + "/platform-settings/command-schedules/SEND_USER_REPORT/history"
                        + "?cpty=" + encodeURIComponent(cptyCode)
                )
            );
        };
    },

    /** Send one counterparty's user-access report now, bypassing its cadence.
     *  Recorded in the same history the scheduled sends write to. */
    RunUserReportNow: (cptyCode, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            return await dispatch(
                fetchWrapper(
                    apiBase + "/platform-settings/command-schedules/SEND_USER_REPORT/run"
                        + "?cpty=" + encodeURIComponent(cptyCode),
                    { method: "POST", headers: { "x-reason": reason || "" } }
                )
            );
        };
    },

    OnboardCpty: (body, reason) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;

            // `body` is the full setup-own-cpty create request (settings incl.
            // cptyCode, knownCptys, partnerCptys, accounts, API-access flags,
            // apiKey). No isExisting => create. uiEnabled defaults on so the
            // Keycloak org is provisioned for UI sign-ups.
            const job = await dispatch(fetchWrapper(apiBase + "/setup-own-cpty", {
                method: "POST",
                headers: { "x-reason": reason || "" },
                body: JSON.stringify({
                    uiEnabled: true,
                    ...body,
                }),
            }));

            dispatch({
                type: Admin.SET_ONBOARD_STATUS,
                onboardStatus: job,
            });

            const deadline = localNow() + POLL_TIMEOUT_MS;
            while (localNow() < deadline) {
                await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

                const status = await dispatch(
                    fetchWrapper(apiBase + "/setup-own-cpty/" + job.jobId)
                );

                dispatch({
                    type: Admin.SET_ONBOARD_STATUS,
                    onboardStatus: status,
                });

                if (status.status === "FAILED") {
                    const msg = (status.errors || []).join(", ") || "Setup failed";
                    throw new Error(msg);
                }

                if (
                    status.status === "COMPLETED" &&
                    status.progress.settingsCommandsPublished > 0 &&
                    status.progress.settingsPropagated >=
                        status.progress.settingsCommandsPublished &&
                    status.progress.directoryPropagated
                ) {
                    return status;
                }
            }

            throw new Error("Setup timed out - settings not propagated");
        };
    },

    ClearOnboardStatus: () => {
        return { type: Admin.SET_ONBOARD_STATUS, onboardStatus: null };
    },

    /**
     * Cancel a scheduled deletion within its grace window. The backend
     * restores access and flips the cpty's lifecycle back to ACTIVE.
     */
    CancelOffboard: (cptyCode) => {
        return async (dispatch, getState) => {
            const url = getState().Auth.adminServiceUrl + "/offboard-cpty/" + cptyCode + "/cancel";
            await dispatch(fetchWrapper(url, { method: "POST" }));
        };
    },

    OffboardCpty: (cptyCode, reason, deleteData = false) => {
        return async (dispatch, getState) => {
            const apiBase = getState().Auth.adminServiceUrl;
            const params = new URLSearchParams();
            if (deleteData) params.set("deleteData", "true");
            if (reason) params.set("reason", reason);
            const query = params.toString() ? "?" + params.toString() : "";
            await dispatch(fetchWrapper(apiBase + "/offboard-cpty/" + cptyCode + query, {
                method: "DELETE",
            }));
        };
    },

    ClearSelectedCpty: () => {
        return { type: Admin.SET_SELECTED_CPTY, cpty: null };
    },

    FetchAuditLog: ({ limit = 50, offset = 0, from, to } = {}) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            let q = `?limit=${limit}&offset=${offset}`;
            if (from) q += `&from=${from}`;
            if (to) q += `&to=${to}`;
            return await dispatch(fetchWrapper(base + "/audit-log" + q));
        };
    },

    FetchFeatureFlags: () => {
        return async (dispatch, getState) => {
            dispatch({ type: Admin.SET_FLAGS_LOADING, loading: true });
            try {
                const base = getState().Auth.adminServiceUrl;
                const resp = await dispatch(fetchWrapper(base + "/flags"));
                dispatch({ type: Admin.SET_FEATURE_FLAGS, flags: resp || [] });
            } catch (error) {
                console.error("Failed to fetch feature flags:", error);
                dispatch({ type: Admin.SET_FLAGS_ERROR, error: error.message });
            } finally {
                dispatch({ type: Admin.SET_FLAGS_LOADING, loading: false });
            }
        };
    },

    ToggleFlag: (hash, flagKey, enabled, reason) => {
        return async (dispatch, getState) => {
            try {
                const base = getState().Auth.adminServiceUrl;
                await dispatch(fetchWrapper(base + "/flags/" + hash + "/toggle", {
                    method: "PUT",
                    headers: { "Content-Type": "application/json", "x-reason": reason || "" },
                    body: JSON.stringify({ flagKey, flagHash: hash, enabled }),
                }));
                const currentFlags = getState().Admin.featureFlags;
                const updated = currentFlags.map(f =>
                    f.hash === hash ? { ...f, enabled } : f
                );
                dispatch({ type: Admin.SET_FEATURE_FLAGS, flags: updated });
            } catch (error) {
                console.error("Failed to toggle flag:", error);
                dispatch({ type: Admin.SET_FLAGS_ERROR, error: error.message });
            }
        };
    },

    FetchFlagHistory: (hash) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            return await dispatch(fetchWrapper(base + "/flags/" + hash + "/history"));
        };
    },

    /**
     * Remove a group or cpty override; the scope falls back to the
     * flag's global state.
     */
    // Set a per-scope flag override. The UI only ever passes CPTY: group overrides
    // are the e2e runner's isolation mechanism, not operator configuration.
    SetFlagOverride: (hash, flagKey, scopeType, scopeValue, enabled) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(base + "/flags/" + hash + "/override", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    flagKey, flagHash: hash, scopeType, scopeValue, enabled,
                }),
            }));
        };
    },

    RemoveFlagOverride: (hash, flagKey, scopeType, scopeValue) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(base + "/flags/" + hash + "/override", {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ flagKey, flagHash: hash, scopeType, scopeValue }),
            }));
        };
    },

    FetchUsers: () => {
        return async (dispatch, getState) => {
            dispatch({ type: Admin.SET_USERS_LOADING, loading: true });
            try {
                const base = getState().Auth.adminServiceUrl;
                const users = await dispatch(fetchWrapper(base + "/users"));
                dispatch({ type: Admin.SET_USERS, users: users || [] });
            } catch (error) {
                console.error("Failed to fetch users:", error);
                dispatch({ type: Admin.SET_USERS_ERROR, error: error.message });
            } finally {
                dispatch({ type: Admin.SET_USERS_LOADING, loading: false });
            }
        };
    },

    // One cpty's users, loaded on-demand when the operator opens Manage
    // Users. Returned to the caller (component-local state), not stored in
    // redux - only one cpty's list is on screen at a time.
    FetchCptyUsers: (code) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            return (await dispatch(fetchWrapper(`${base}/users/cpty/${encodeURIComponent(code)}`))) || [];
        };
    },

    // One user's auth-action activity timeline, newest first. Component-local
    // (only one user's history is on screen at a time), not stored in redux.
    FetchUserActivity: (realm, userId) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            return (await dispatch(fetchWrapper(
                `${base}/users/${encodeURIComponent(realm)}/${encodeURIComponent(userId)}/activity`,
            ))) || [];
        };
    },

    // One user's active BFF sessions (component-local, shown in the row expand).
    FetchUserSessions: (realm, userId) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            return (await dispatch(fetchWrapper(
                `${base}/users/${encodeURIComponent(realm)}/${encodeURIComponent(userId)}/sessions`,
            ))) || [];
        };
    },

    // Revoke one session (sign it out).
    RevokeUserSession: (realm, userId, sessionId) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(
                `${base}/users/${encodeURIComponent(realm)}/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(sessionId)}`,
                { method: "DELETE" },
            ));
        };
    },

    // Revoke every session for the user (sign out everywhere).
    RevokeAllUserSessions: (realm, userId) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(
                `${base}/users/${encodeURIComponent(realm)}/${encodeURIComponent(userId)}/sessions`,
                { method: "DELETE" },
            ));
        };
    },

    // Clear a user's enrolled TOTP so they set up a fresh authenticator at their
    // next sign-in. Scoped to one user: turning the counterparty's MFA off also
    // clears TOTP, but for every one of its users at once.
    ResetUserTotp: (realm, userId, reason) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            return dispatch(fetchWrapper(
                `${base}/users/${encodeURIComponent(realm)}/${encodeURIComponent(userId)}/totp`,
                { method: "DELETE", headers: { "x-reason": reason || "" } },
            ));
        };
    },

    // Create a counterparty user directly, the operator-driven counterpart to
    // self-signup. The backend enforces the cpty's email-domain whitelist and
    // requires its UI API to be enabled, so a rejection here is a real
    // precondition rather than a validation the form should duplicate.
    CreateCptyUser: ({ cptyCode, email, firstName, lastName, password, role, emailVerified }, reason) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(base + "/create-user", {
                method: "POST",
                headers: { "Content-Type": "application/json", "x-reason": reason || "" },
                body: JSON.stringify({
                    cptyCode, email, firstName, lastName, password, role, emailVerified,
                }),
            }));
        };
    },

    // Cpty users only: the backend endpoint is fixed to the truweb realm,
    // so platform accounts can never be re-roled through it.
    SetCptyUserRole: (userId, role, reason) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(`${base}/users/cpty/${encodeURIComponent(userId)}/role`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", "x-reason": reason || "" },
                body: JSON.stringify({ role }),
            }));
        };
    },

    SetUserEnabled: (realm, userId, enabled, reason) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(base + `/users/${realm}/${userId}/enabled`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", "x-reason": reason || "" },
                body: JSON.stringify({ enabled }),
            }));
            await dispatch(AdminActions.FetchUsers());
        };
    },

    DeleteUser: (realm, userId, reason) => {
        return async (dispatch, getState) => {
            const base = getState().Auth.adminServiceUrl;
            await dispatch(fetchWrapper(base + `/users/${realm}/${userId}`, {
                method: "DELETE",
                headers: { "x-reason": reason || "" },
            }));
            await dispatch(AdminActions.FetchUsers());
        };
    },
};
